import { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Users, Download, ChevronDown, ChevronRight } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import TechProfileDrawer from '../components/TechProfileDrawer';
import { exportCsv } from '../utils/exportCsv';

function TechRow({ tech, onOpenProfile }) {
  const [open, setOpen] = useState(false);
  const flaggedItems = tech.items.filter(i => i.flagged > 0);

  return (
    <>
      <tr className="border-b border-gray-50 hover:bg-gray-50/60 cursor-pointer" onClick={() => setOpen(o => !o)}>
        <td className="px-4 py-2.5 text-gray-400">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="px-4 py-2.5 font-medium text-[#E8761A] hover:text-[#3D2B1A] hover:underline"
          onClick={e => { e.stopPropagation(); onOpenProfile(tech); }}>
          {tech.primary_tech}
        </td>
        <td className="px-4 py-2.5 text-center text-gray-600">{tech.total_completed}</td>
        <td className="px-4 py-2.5 text-center text-green-600 font-semibold">{tech.passed ?? 0}</td>
        <td className="px-4 py-2.5 text-center text-red-500 font-semibold">{tech.flagged ?? 0}</td>
        <td className="px-4 py-2.5 text-center">
          {tech.pass_rate != null ? (
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
              tech.pass_rate >= 90 ? 'bg-green-100 text-green-700' :
              tech.pass_rate >= 75 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
              {tech.pass_rate}%
            </span>
          ) : <span className="text-gray-300 text-xs">—</span>}
        </td>
        <td className="px-4 py-2.5 text-center text-gray-500">{tech.avg_score != null ? `${tech.avg_score}%` : '—'}</td>
        <td className="px-4 py-2.5 text-xs text-gray-400">
          {flaggedItems.length === 0 ? '—' :
            flaggedItems.map(i => `${i.item} (${i.flagged})`).join(', ')}
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50/40">
          <td></td>
          <td colSpan={7} className="px-4 py-3">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {tech.items.map(i => (
                <div key={i.field} className="bg-white border border-gray-100 rounded-lg px-3 py-2 flex items-center justify-between">
                  <span className="text-xs text-gray-600">{i.item}</span>
                  <span className="flex items-center gap-2 text-xs font-semibold">
                    <span className="text-red-500">{i.flagged} flagged</span>
                    <span className="text-gray-300">/</span>
                    <span className="text-green-600">{i.not_flagged} ok</span>
                  </span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function TechStats() {
  const [data,     setData]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [profile, setProfile] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      const r = await api.get('/reports/tech-stats', { params });
      setData(r.data || []);
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const branchMap = {};
  data.forEach(t => {
    if (!branchMap[t.branch]) branchMap[t.branch] = [];
    branchMap[t.branch].push(t);
  });
  const branches = Object.keys(branchMap).sort();

  function handleExport() {
    const rows = [];
    const source = branchFilter ? data.filter(t => t.branch === branchFilter) : data;
    source.forEach(t => {
      const base = {
        Branch: t.branch, Technician: t.primary_tech,
        'Total Completed': t.total_completed, Passed: t.passed ?? 0,
        Flagged: t.flagged ?? 0, 'Pass Rate': t.pass_rate != null ? `${t.pass_rate}%` : '',
        'Avg Score': t.avg_score != null ? `${t.avg_score}%` : '',
      };
      t.items.forEach(i => {
        base[`${i.item} - Flagged`]     = i.flagged;
        base[`${i.item} - Not Flagged`] = i.not_flagged;
      });
      rows.push(base);
    });
    exportCsv(`technician-stats${branchFilter ? `-${branchFilter}` : ''}.csv`, rows);
  }

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      {profile && (
        <TechProfileDrawer tech={profile.primary_tech} branch={profile.branch} stats={profile} onClose={() => setProfile(null)} />
      )}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users size={22} className="text-[#E8761A]" /> Technician Stats
          </h1>
          <p className="text-sm text-gray-500 mt-1">All technicians by branch — pass/flag counts and per-item breakdown</p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
          <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)}
            className="text-xs bg-white border border-gray-200 rounded-lg px-2 py-2 text-gray-600 outline-none">
            <option value="">All Branches</option>
            {branches.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <button onClick={handleExport} disabled={!data.length}
            className="flex items-center gap-1.5 px-3 py-2 text-xs bg-[#3D2B1A] hover:bg-[#E8761A] text-white rounded-lg disabled:opacity-40 transition-colors">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-400">Loading...</div>
      ) : branches.length === 0 ? (
        <div className="p-12 text-center text-gray-400">No completed audits found for this range</div>
      ) : (
        branches.map(branch => (
          <div key={branch} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-3 border-b border-gray-100 bg-gray-50">
              <h2 className="text-sm font-semibold text-gray-700">{branch}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 text-xs">
                    <th className="px-4 py-2"></th>
                    <th className="px-4 py-2 font-medium">Technician</th>
                    <th className="px-4 py-2 font-medium text-center">Completed</th>
                    <th className="px-4 py-2 font-medium text-center">Passed</th>
                    <th className="px-4 py-2 font-medium text-center">Flagged</th>
                    <th className="px-4 py-2 font-medium text-center">Pass Rate</th>
                    <th className="px-4 py-2 font-medium text-center">Avg Score</th>
                    <th className="px-4 py-2 font-medium">Flagged Items</th>
                  </tr>
                </thead>
                <tbody>
                  {branchMap[branch].map(t => <TechRow key={t.primary_tech} tech={t} onOpenProfile={setProfile} />)}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
